
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import WebsiteMigrationsRepositoryInterface, { WebsiteMigration } from '@/services/WebsiteMigrations/interfaces';
import WebsiteRepositoryInterface from '@/services/Website/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';
import {
  LIST_FOOTER_PROPS,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    PageHeader,
    StatusChip,
  },
  inject: [
    'websiteMigrationsRepository',
    'websiteRepository',
  ],
})
export default class WebsiteMigrationsList extends Vue {
  public loading = true;

  public options = defaultListOptions();

  public footerProps = LIST_FOOTER_PROPS;

  public totalMigrationsCount = 0;

  public migrationSearch = '';

  public migrations: WebsiteMigration[] = [];

  public websitesDomainMap: { [key: number]: string } = {};

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id', width: '80px' },
    { text: 'Source', value: 'sourceWebsiteId' },
    { text: 'Target', value: 'targetWebsiteId' },
    { text: 'Type', value: 'type' },
    { text: 'Status', value: 'status' },
  ];

  public websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.loadMigrations();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'website_migrations_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.loadMigrations();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.loadMigrations();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'website_migrations_list') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.loadMigrations();
  }

  public async loadMigrations(): Promise<void> {
    this.loading = true;

    try {
      const search = this.migrationSearch.toLowerCase();

      let status = '';
      if (['done', 'pending', 'error', 'failed'].includes(search)) {
        status = search;
      }

      let type = '';
      if (['main to main', 'sub to sub', 'rename'].includes(search)) {
        type = search;

        if (search !== 'rename') {
          type = `migrate-${search.split(' ').join('-')}`;
        }
      }

      const response = await this.websiteMigrationsRepository.getAllMigrations(
        this.options.page,
        effectivePageSize(this.options.itemsPerPage),
        undefined,
        undefined,
        type,
        status,
      );

      if (response.totalItems > 0) {
        const sourceWebsitesIds: number[] = [];
        const targetWebsitesIds: number[] = [];

        response.migrations.forEach((m) => {
          if (!sourceWebsitesIds.includes(m.sourceWebsiteId)) {
            sourceWebsitesIds.push(m.sourceWebsiteId);
          }

          if (!targetWebsitesIds.includes(m.targetWebsiteId)) {
            targetWebsitesIds.push(m.targetWebsiteId);
          }
        });

        const websites = await Promise.all([
          ...(await this.websiteRepository.getWebsitesByIds(sourceWebsitesIds)),
          ...(await this.websiteRepository.getWebsitesByIds(targetWebsitesIds)),
        ]);

        websites.forEach((website) => {
          this.websitesDomainMap[website.id] = website.domain;
        });
      }

      this.totalMigrationsCount = response.totalItems;
      this.migrations = response.migrations;
    } catch (error) {
      this.$errorDialog.show(error.response?.data?.error || error.message);
    } finally {
      this.loading = false;
    }
  }

  public async viewMigration(migration: WebsiteMigration): Promise<void> {
    await this.$router.push({
      name: 'view_website_migration',
      params: { id: migration.id.toString() },
    });
  }

  public getTypeString(type: string): string {
    return type
      .replaceAll('migrate', '')
      .replaceAll('-', ' ')
      .trim()
      .toUpperCase();
  }
}
